import React, { Component } from "react";
import { Container, Row, Col, Nav, NavItem, NavLink, Table } from "reactstrap";
import {
  CHECKOUT_BASE,
  WIDGET_CDN,
  NAV,
  WIDGET_SCRIPT_ATTRS,
  CHECKOUT_PARAMS,
  JS_API,
} from "./merchantApiContent";
import { CodeBlock, CompareTable, SidebarActions } from "./apiDocsShared";
import "./api-docs.css";

class EmbedDocs extends Component {
  constructor(props) {
    super(props);
    this.state = { activeTab: "overview" };
  }

  toggleTab = (tab) => {
    if (this.state.activeTab !== tab) this.setState({ activeTab: tab });
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      /* noop */
    }
  };

  renderOverview() {
    return (
      <div className="qt-api-docs__section">
        <h2>Website embed</h2>
        <p className="text-muted">
          Add live delivery quotes to your online store with one script tag and QikTruck-hosted checkout.
          Shoppers stay on your product page for pricing; payment and booking happen on qiktruck.co.za.
        </p>

        <div className="qt-api-docs__callout qt-api-docs__callout--info">
          <strong>No secret API key in your storefront.</strong> Your public <code>site_id</code> (
          <code>qt_pub_…</code>) and verified domain are enough. Configure domains and warehouses in{" "}
          <a href="/integrations">Integrations</a> before embedding.
        </div>

        <h3>How it fits together</h3>
        <div className="qt-api-flow">
          <span className="qt-api-flow__step">1. Integrations - domain &amp; warehouses</span>
          <span className="qt-api-flow__arrow">→</span>
          <span className="qt-api-flow__step">2. Storefront - paste widget script</span>
          <span className="qt-api-flow__arrow">→</span>
          <span className="qt-api-flow__step">3. Shopper - hosted checkout</span>
          <span className="qt-api-flow__arrow">→</span>
          <span className="qt-api-flow__step">4. QikTruck - quote, book, dispatch</span>
        </div>

        <h3>Quick start</h3>
        <ol className="text-muted" style={{ fontSize: 14 }}>
          <li>Open <a href="/integrations">Integrations</a> → Website Widget.</li>
          <li>Add and verify your domain (DNS TXT record).</li>
          <li>Save warehouse pickup in Configuration.</li>
          <li>Copy the install snippet and paste it on product pages.</li>
        </ol>

        <h3>URLs</h3>
        <Table bordered size="sm" className="qt-api-table bg-white">
          <tbody>
            <tr>
              <td><strong>Hosted checkout</strong></td>
              <td><code>{CHECKOUT_BASE}</code></td>
            </tr>
            <tr>
              <td><strong>Widget script (CDN)</strong></td>
              <td><code>{WIDGET_CDN}</code></td>
            </tr>
          </tbody>
        </Table>

        <p className="text-muted small mt-3">
          ERP or TMS integrations? See the{" "}
          <a href="/api-docs?product=partner">Partner API</a> (separate product, API keys required).
        </p>
      </div>
    );
  }

  renderWidgetEmbed() {
    return (
      <div className="qt-api-docs__section">
        <h2>Widget embed</h2>
        <p className="text-muted">
          Drop one script tag on product pages. The widget loads your saved pickup and defaults, then opens
          hosted checkout in a modal iframe.
        </p>
        <CodeBlock label="Minimal embed (floating bubble)">
{`<script src="${WIDGET_CDN}"
        data-qt-site="qt_pub_YOUR_SITE_ID"
        data-qt-mode="bubble"
        data-qt-label="Get delivery price"
        data-qt-description="3-Seater Couch"></script>`}
        </CodeBlock>
        <CodeBlock label="Inline on product page">
{`<div data-qt-widget></div>
<script src="${WIDGET_CDN}"
        data-qt-site="qt_pub_YOUR_SITE_ID"
        data-qt-mode="inline"
        data-qt-target="[data-qt-widget]"></script>`}
        </CodeBlock>
        <CodeBlock label="Custom trigger button">
{`<button type="button" data-qt-open>Calculate delivery</button>
<script src="${WIDGET_CDN}" data-qt-site="qt_pub_..." data-qt-mode="button"></script>`}
        </CodeBlock>

        <h3>Script attributes</h3>
        <Table bordered responsive size="sm" className="qt-api-table bg-white">
          <thead className="table-light">
            <tr><th>Attribute</th><th>Description</th><th>Default</th></tr>
          </thead>
          <tbody>
            {WIDGET_SCRIPT_ATTRS.map((r, i) => (
              <tr key={i}><td><code>{r[0]}</code></td><td>{r[1]}</td><td><code>{r[2]}</code></td></tr>
            ))}
          </tbody>
        </Table>

        <h3>JavaScript API</h3>
        <Table bordered size="sm" className="qt-api-table bg-white">
          <tbody>
            {JS_API.map((row) => (
              <tr key={row.sig}>
                <td><code>{row.sig}</code></td>
                <td>{row.desc}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        <CodeBlock label="Open with checkout prefill">
{`QikTruck.open({
  description: "King-size bed",
  destination: { lat: -33.918, lng: 18.421, address: "Long St, Cape Town" },
  email: "shopper@example.com",
  mobile: "0821234567",
  firstname: "Sam",
  lastname: "Shopper",
  truckType: "1tonopen",
  onQuoted: function (data) { console.log("Quoted", data); }
});`}
        </CodeBlock>
      </div>
    );
  }

  renderCheckoutUrl() {
    return (
      <div className="qt-api-docs__section">
        <h2>Hosted checkout URL</h2>
        <p className="text-muted">
          Direct link or iframe when not using the CDN widget. Pickup comes from your saved warehouse in
          Integrations; pass delivery details in the query string.
        </p>
        <CodeBlock label="Example">
          {CHECKOUT_BASE + "?site=qt_pub_...&description=3-Seater+Couch&deliverylat=-33.918&deliverylng=18.4212&deliveryaddress=Long+Street"}
        </CodeBlock>
        <h3>Query parameters</h3>
        <Table bordered responsive size="sm" className="qt-api-table bg-white">
          <thead className="table-light">
            <tr><th>Param</th><th>Description</th><th>Notes</th></tr>
          </thead>
          <tbody>
            {CHECKOUT_PARAMS.map((r, i) => (
              <tr key={i}><td><code>{r[0]}</code></td><td>{r[1]}</td><td>{r[2]}</td></tr>
            ))}
          </tbody>
        </Table>
        <div className="qt-api-docs__callout">
          <strong>Express checkout:</strong> When description, delivery coords, email, mobile, and names are
          all prefilled, the shopper sees a one-screen confirm - ideal for WooCommerce/Shopify checkout handoff.
        </div>
      </div>
    );
  }

  renderContent() {
    switch (this.state.activeTab) {
      case "compare": return <CompareTable />;
      case "widget-embed": return this.renderWidgetEmbed();
      case "checkout-url": return this.renderCheckoutUrl();
      default: return this.renderOverview();
    }
  }

  render() {
    const { productSwitcher } = this.props;
    return (
      <div className="qt-api-docs">
        <div className="qt-api-docs__hero qt-api-docs__hero--embed">
          <Container>
            {productSwitcher}
            <span className="qt-api-docs__badge qt-api-docs__badge--embed">Website embed</span>
            <h1>Delivery quotes on your store</h1>
            <p className="lead mb-0">
              Script tag, hosted checkout, and JavaScript helpers for e-commerce stores.
            </p>
          </Container>
        </div>
        <Container fluid>
          <Row>
            <Col lg={3} className="qt-api-docs__sidebar d-none d-lg-block">
              <div className="sticky-top pt-3" style={{ top: "1rem" }}>
                <Nav className="flex-column">
                  {NAV.map((item) => (
                    <NavItem key={item.id}>
                      <NavLink
                        className={this.state.activeTab === item.id ? "active" : ""}
                        onClick={() => this.toggleTab(item.id)}
                      >
                        {item.label}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
                <SidebarActions
                  secondaryHref="/api-docs?product=partner"
                  secondaryLabel="Partner API docs"
                  primaryHref="/integrations?type=widget"
                  primaryLabel="Open Integrations hub"
                />
              </div>
            </Col>
            <Col lg={9} className="qt-api-docs__main">
              <div className="d-lg-none mb-3">
                <select
                  className="form-select"
                  value={this.state.activeTab}
                  onChange={(e) => this.toggleTab(e.target.value)}
                >
                  {NAV.map((item) => (
                    <option key={item.id} value={item.id}>{item.label}</option>
                  ))}
                </select>
              </div>
              {this.renderContent()}
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

export default EmbedDocs;
